import React, { useEffect, useState, useRef } from "react";
import {
  Container, Typography, Box, Paper, Slider,
  FormControlLabel, Switch, Button, useMediaQuery,
  FormControl, InputLabel, Select, MenuItem
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import AuthHeader from "../components/AuthHeader";

function WordViewerPage({ API_BASE, AUDIO_BASE }) {
  const isSmallScreen = useMediaQuery("(max-width:600px)");
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const searchParams = new URLSearchParams(location.search);
  const wordFilterParam = searchParams.get("word");
  const hasWordFilter = Boolean(wordFilterParam);

  const [words, setWords] = useState([]);
  const [weekOptions, setWeekOptions] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState("");
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const [loopAll, setLoopAll] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(null);
  const [playingKey, setPlayingKey] = useState(null);
  const audioRefs = useRef({});
  const [audioFileExists, setAudioFileExists] = useState({});

  // 音声再生ログ関連
  const [currentPlayLog, setCurrentPlayLog] = useState(null);

  // ログイン状態チェック
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/word_viewer' }, replace: true });
    }
  }, [isAuthenticated, navigate]);

  // 音声再生ログ開始
  const startAudioPlayLog = async (word) => {
    // db_user_idが無い場合はログアウトしてログイン画面へリダイレクト
    if (!user?.db_user_id) {
      console.warn('⚠️ db_user_idが無いため、ログアウトしてログイン画面へリダイレクトします。');
      logout();
      return;
    }

    try {
      const logData = {
        session_id: String(user.db_user_id),
        content_type: 'word',
        content_id: word.id,
        audio_file_name: word.audio_file_name,
        is_video: 0, // 音声の場合は0
        play_start_time: new Date().toISOString(),
        playback_rate: playbackRate
      };

      const response = await fetch(`${API_BASE}/audio_play_logs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(logData)
      });

      const data = await response.json();
      if (data.success) {
        setCurrentPlayLog({
          log_id: data.log_id,
          start_time: new Date(),
          ...logData
        });
        console.log('★単語音声再生ログ開始:', data.log_id);
      }
    } catch (error) {
      console.error('★単語音声ログ開始エラー:', error);
    }
  };

  // 音声再生ログ完了
  const completeCurrentPlayLog = async () => {
    if (!currentPlayLog) return;

    try {
      const endTime = new Date();
      const durationSeconds = (endTime - currentPlayLog.start_time) / 1000;

      const updateData = {
        ...currentPlayLog,
        play_end_time: endTime.toISOString(),
        duration_seconds: durationSeconds
      };

      await fetch(`${API_BASE}/audio_play_logs/${currentPlayLog.log_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });

      console.log('★単語音声再生ログ完了:', durationSeconds, '秒');
      setCurrentPlayLog(null);
    } catch (error) {
      console.error('★単語音声ログ完了エラー:', error);
    }
  };

  useEffect(() => {
    if (hasWordFilter) {
      fetch(`${API_BASE}/joined_transcribed_words_filtered?word=${wordFilterParam}`)
        .then((res) => res.json())
        .then((data) => setWords(Array.isArray(data) ? data : []))
        .catch((err) => console.error("API fetch error:", err));
    } else {
      fetch(`${API_BASE}/transcription_weeks`)
        .then((res) => res.json())
        .then((data) => {
          const filtered = Array.isArray(data)
            ? data.filter((w) => w.category === "Words").map((w) => w.week_num)
            : [];
          const reversed = [...filtered].reverse();
          setWeekOptions(reversed);
          if (reversed.length > 0) {
            setSelectedWeek(reversed[0]);
          }
        })
        .catch((err) => console.error("week fetch error:", err));
    }
  }, []);

  useEffect(() => {
    if (!hasWordFilter && selectedWeek) {
      fetch(`${API_BASE}/joined_transcribed_words_by_week/${selectedWeek}`)
        .then((res) => res.json())
        .then((data) => setWords(Array.isArray(data) ? data : []))
        .catch((err) => console.error("API fetch error:", err));
    }
  }, [selectedWeek, hasWordFilter]);

  useEffect(() => {
    Object.values(audioRefs.current).forEach((audio) => {
      if (audio) audio.playbackRate = playbackRate;
    });
  }, [playbackRate, words]);

  const checkAudioFileExists = async (filename) => {
    try {
      const response = await fetch(`${AUDIO_BASE}/audio_clipped/${filename}`, { method: 'HEAD' });
      return response.ok;
    } catch (error) {
      return false;
    }
  };

  useEffect(() => {
    const checkAllAudioFiles = async () => {
      const existsMap = {};
      for (const item of words) {
        if (item.audio_file_name) {
          const exists = await checkAudioFileExists(item.audio_file_name);
          existsMap[item.audio_file_name] = exists;
        }
      }
      setAudioFileExists(existsMap);
    };

    if (words.length > 0) {
      checkAllAudioFiles();
    }
  }, [words, AUDIO_BASE]);

  const stopAllAudio = async () => {
    // 現在の再生ログを完了
    if (currentPlayLog) {
      await completeCurrentPlayLog();
    }

    Object.values(audioRefs.current).forEach((audio) => {
      if (audio && !audio.paused) {
        audio.pause();
        audio.currentTime = 0;
      }
    });
    setPlayingKey(null);
    setCurrentIndex(null);
  };

  const handlePlay = async (index) => {
    await stopAllAudio();
    
    const key = `${index}`;
    const audio = audioRefs.current[key];
    const currentWord = words[index];
    
    if (audio && currentWord) {
      audio.play();
      setPlayingKey(key);
      setCurrentIndex(index);
      
      // 音声再生ログ開始
      await startAudioPlayLog(currentWord);
    }
  };

  const handleEnded = async () => {
    // 音声終了時の処理
    if (currentPlayLog) {
      await completeCurrentPlayLog();
    }

    if (loopAll && playingKey !== null) {
      const audio = audioRefs.current[playingKey];
      if (audio) {
        const currentWord = words[currentIndex];
        if (currentWord) {
          await startAudioPlayLog(currentWord);
        }
        audio.currentTime = 0;
        audio.play();
      }
    } else {
      // 既にcompleteCurrentPlayLog()を呼んだので、stopAllAudioは呼ばずに直接状態をリセット
      setPlayingKey(null);
      setCurrentIndex(null);
    }
  };

  // ログイン中のみ表示
  if (!isAuthenticated) {
    return null; // リダイレクト中は何も表示しない
  }

  return (
    <>
      <AuthHeader />
      <Container maxWidth="md" sx={{ paddingBottom: isSmallScreen ? 24 : 14 }}>

      {!hasWordFilter && (
        <Box sx={{ marginBottom: 2 }}>
          <FormControl fullWidth>
            <InputLabel id="week-label">週番号を選択</InputLabel>
            <Select
              labelId="week-label"
              value={selectedWeek}
              label="週番号を選択"
              onChange={(e) => setSelectedWeek(e.target.value)}
            >
              {weekOptions.map((week, i) => (
                <MenuItem key={i} value={week}>{week}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      <Box display="flex" flexDirection="column" gap={3}>
        {words.map((item, index) => (
          <Paper
            key={item.id || index}
            sx={{ padding: 2, backgroundColor: currentIndex === index ? '#e3f2fd' : 'inherit' }}
          >
            <Typography variant="h4" gutterBottom>
              {item.word}
            </Typography>
            {item.word_jp && (
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {item.word_jp}
              </Typography>
            )}
            {playingKey === `${index}` ? (
              <Button variant="outlined" color="error" onClick={stopAllAudio}>停止</Button>
            ) : (
              <Button 
                variant="contained" 
                onClick={() => handlePlay(index)}
                disabled={false}
                title={audioFileExists[item.audio_file_name] === false ? "音声ファイルが見つかりません" : ""}
              >
                再生
              </Button>
            )}
            <audio
              style={{ display: "none" }}
              ref={(el) => (audioRefs.current[`${index}`] = el)}
              onEnded={handleEnded}
            >
              <source src={`${AUDIO_BASE}/audio_clipped/${item.audio_file_name}`} type="audio/mpeg" />
            </audio>
          </Paper>
        ))}
      </Box>

      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          width: "100%",
          backgroundColor: "#fff",
          borderTop: "1px solid #ccc",
          padding: 2,
          display: "flex",
          flexDirection: isSmallScreen ? "column" : "row",
          gap: 2,
          justifyContent: "center",
          alignItems: "center",
          zIndex: 1000,
        }}
      >
        <Box minWidth={200}>
          <Typography gutterBottom>再生速度：{playbackRate.toFixed(1)}x</Typography>
          <Slider
            value={playbackRate}
            onChange={(_, val) => setPlaybackRate(val)}
            min={0.5}
            max={1.5}
            step={0.1}
            valueLabelDisplay="auto"
            marks={[{ value: 0.5, label: "0.5x" }, { value: 1.0, label: "1.0x" }, { value: 1.5, label: "1.5x" }]}
          />
        </Box>
        <FormControlLabel
          control={<Switch checked={loopAll} onChange={(e) => setLoopAll(e.target.checked)} />}
          label="ループ再生"
        />
      </Box>
      </Container>
    </>
  );
}

export default WordViewerPage;